import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import api from "../../utils/api";
import Swal from "sweetalert2";
import FormQuestions from "../forms/FormQuestions";
import { motion } from "framer-motion";

const FormsDetails = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [answerStatus, setAnswerStatus] = useState(null);

  useEffect(() => {
    const fetchForm = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/forms/${code}`);
        setForm(response.data.data);
      } catch (error) {
        Swal.fire("Error", "Failed to load form details.", "error");
        navigate("/forms");
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [code, navigate]);

  const handleAnswerFeedback = (isCorrect) => {
    setAnswerStatus(isCorrect ? "correct" : "incorrect");
    setTimeout(() => setAnswerStatus(null), 800);
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full"></div>
      </div>
    );

  if (!form)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 text-xl">
        Form not found.
      </div>
    );

  const isFree = form.price === "0.00";

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8">
        <LanguageLink
          to="/forms"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 text-sm font-medium"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Forms
        </LanguageLink>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-xl p-8"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
              <p className="text-sm text-gray-500 mt-1">Code: {form.code}</p>
            </div>
            <div className="flex items-center gap-2">
              {form.is_paid ? (
                <span className="bg-green-100 text-green-800 text-sm font-bold px-4 py-2 rounded-full flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Already Paid
                </span>
              ) : isFree ? (
                <span className="bg-blue-100 text-blue-800 text-sm font-bold px-4 py-2 rounded-full">
                  Free
                </span>
              ) : (
                <span className="bg-green-50 text-green-700 text-sm font-bold px-4 py-2 rounded-full border border-green-200">
                  ${form.price}
                </span>
              )}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Description
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
              {form.description}
            </p>
          </div>

          {form.pdf_template_path && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-2">
                Download Original Form (PDF)
              </p>
              <a
                href={`https://api.citznhub.com/storage/${form.pdf_template_path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <svg
                  className="w-5 h-5 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                View File
              </a>
            </div>
          )}

          <div className="mb-8 p-5 bg-yellow-50 rounded-lg border border-yellow-200">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I agree to the{" "}
                <LanguageLink
                  to="/terms-of-service"
                  className="text-blue-600 hover:underline font-medium"
                >
                  Terms and Conditions
                </LanguageLink>{" "}
                and{" "}
                <LanguageLink
                  to="/privacy-policy"
                  className="text-blue-600 hover:underline font-medium"
                >
                  Privacy Policy
                </LanguageLink>
                .
              </span>
            </label>
          </div>

          <motion.div
            animate={{
              scale: answerStatus === "correct" ? [1, 1.02, 1] : 1,
              backgroundColor:
                answerStatus === "correct"
                  ? ["#ffffff", "#ecfdf5", "#ffffff"]
                  : answerStatus === "incorrect"
                    ? ["#ffffff", "#fef2f2", "#ffffff"]
                    : "#ffffff",
            }}
            transition={{ duration: 0.5 }}
          >
            <FormQuestions
              formCode={form.code}
              isAgreed={agreed}
              formPrice={form.price}
              formFields={form.fields}
              onAnswerFeedback={handleAnswerFeedback}
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default FormsDetails;


// 